import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../../../lib/toast';
import { API, auth, errMsg, PrimaryBtn, Field, inputCls } from '../finance/shared';

const BLANK = { label: '', price_per_hour: '', capacity: '', open_time: '', close_time: '', default_duration_minutes: '' };

export default function VenueCourtsModal({ venue, onClose }) {
  const [courts, setCourts] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/venues/${venue.id}/courts`, auth());
      setCourts(res.data || []);
    } catch { setCourts([]); }
  }, [venue.id]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.label.trim()) { toast.error('اسم المقاس مطلوب'); return; }
    setSaving(true);
    const payload = {
      label: form.label, price_per_hour: form.price_per_hour === '' ? null : Number(form.price_per_hour), capacity: form.capacity === '' ? null : Number(form.capacity),
      open_time: form.open_time || null, close_time: form.close_time || null,
      default_duration_minutes: form.default_duration_minutes === '' ? null : Number(form.default_duration_minutes),
    };
    try {
      if (editingId) await axios.put(`${API}/venues/${venue.id}/courts/${editingId}`, payload, auth());
      else await axios.post(`${API}/venues/${venue.id}/courts`, payload, auth());
      setForm(BLANK);
      setEditingId(null);
      load();
    } catch (e) { toast.error(errMsg(e, 'فشل حفظ المقاس')); } finally { setSaving(false); }
  };

  const remove = async (courtId) => {
    try {
      await axios.delete(`${API}/venues/${venue.id}/courts/${courtId}`, auth());
      toast.success('تم حذف المقاس');
      load();
    } catch (e) { toast.error(errMsg(e, 'فشل الحذف')); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="venue-courts-modal">
      <div className="w-full max-w-lg rounded-xl bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <h4 className="font-bold text-slate-900">مقاسات/كورتات ملعب «{venue.name}»</h4>
          <button onClick={onClose} data-testid="venue-courts-close-btn" className="text-slate-400 hover:text-slate-600">✕</button>
        </div>
        <p className="mb-4 text-xs text-slate-500">لو كان لهذا الملعب أكثر من مقاس (مثال: نصف ملعب 5×5 وملعب كامل 11×11)، عرّف كل مقاس بسعره الخاص هنا ليختار الزائر من بينها عند الحجز.</p>

        {courts === null ? (
          <div className="h-16 animate-pulse rounded-lg bg-slate-100" />
        ) : courts.length === 0 ? (
          <p className="mb-4 rounded-lg bg-slate-50 p-3 text-center text-xs text-slate-400" data-testid="venue-courts-empty">لا توجد مقاسات إضافية — الملعب بمقاس واحد فقط حالياً</p>
        ) : (
          <div className="mb-4 space-y-2" data-testid="venue-courts-list">
            {courts.map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-lg border border-slate-200 p-2.5" data-testid={`venue-court-row-${c.id}`}>
                <div>
                  <p className="text-sm font-semibold text-slate-800">{c.label}</p>
                  <p className="text-xs text-slate-500">{c.price_per_hour ? `${c.price_per_hour} ر.س/ساعة` : ''} {c.capacity ? `• سعة ${c.capacity}` : ''} {c.open_time && c.close_time ? `• ${c.open_time}-${c.close_time}` : ''} {c.default_duration_minutes ? `• مدة ${c.default_duration_minutes} د` : ''}</p>
                </div>
                <div className="flex gap-1.5">
                  <button onClick={() => { setEditingId(c.id); setForm({ label: c.label, price_per_hour: c.price_per_hour ?? '', capacity: c.capacity ?? '', open_time: c.open_time ?? '', close_time: c.close_time ?? '', default_duration_minutes: c.default_duration_minutes ?? '' }); }} data-testid={`venue-court-edit-${c.id}`} className="rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600">تعديل</button>
                  <button onClick={() => remove(c.id)} data-testid={`venue-court-delete-${c.id}`} className="rounded-md border border-red-200 px-2 py-1 text-xs font-semibold text-red-600">حذف</button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-3 gap-2 rounded-lg border border-dashed border-slate-300 p-3">
          <Field label="اسم المقاس"><input className={inputCls} value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="5×5" data-testid="venue-court-form-label" /></Field>
          <Field label="السعر/ساعة"><input type="number" className={inputCls} value={form.price_per_hour} onChange={(e) => setForm({ ...form, price_per_hour: e.target.value })} data-testid="venue-court-form-price" /></Field>
          <Field label="السعة"><input type="number" className={inputCls} value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} data-testid="venue-court-form-capacity" /></Field>
          <Field label="من ساعة"><input type="time" className={inputCls} value={form.open_time} onChange={(e) => setForm({ ...form, open_time: e.target.value })} data-testid="venue-court-form-open-time" /></Field>
          <Field label="إلى ساعة"><input type="time" className={inputCls} value={form.close_time} onChange={(e) => setForm({ ...form, close_time: e.target.value })} data-testid="venue-court-form-close-time" /></Field>
          <Field label="مدة افتراضية (دقيقة)"><input type="number" className={inputCls} value={form.default_duration_minutes} onChange={(e) => setForm({ ...form, default_duration_minutes: e.target.value })} placeholder="60" data-testid="venue-court-form-duration" /></Field>
          <div className="col-span-3 flex justify-end gap-2">
            {editingId && <button onClick={() => { setEditingId(null); setForm(BLANK); }} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600">إلغاء التعديل</button>}
            <PrimaryBtn onClick={save} disabled={saving} data-testid="venue-court-form-submit">{editingId ? 'حفظ التعديل' : 'إضافة مقاس'}</PrimaryBtn>
          </div>
        </div>
      </div>
    </div>
  );
}
